import { useEffect, useMemo } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import APIService from '_app/services/api';
import ROUTES from '_app/pages/routes';
import Utils from '_app/utils';

import { clearSessionError, fetchProfileThunk, setPreviousRoute, setRouteContext } from './sessionSlice';
import { publicIdSelector, previousRouteSelector, sessionErrorSelector, userSelector } from './selectors';
import * as sessionApi from './sessionApi';

const logoRedirectRoutes = ROUTES.filter((route) => route.logoRedirect);

const getRouteContext = (pathname) => {
  const matchedRoute = logoRedirectRoutes.find((route) => !!matchPath(pathname, route));

  if (!matchedRoute) {
    return { publicId: null, showNavBar: true };
  }

  const match = matchPath(pathname, matchedRoute);

  return {
    publicId: match?.params?.publicId || null,
    showNavBar: Boolean(match?.params?.propertyId),
  };
};

export const useSessionRouting = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const routeContext = useMemo(() => getRouteContext(location.pathname), [location.pathname]);

  useEffect(() => {
    dispatch(setRouteContext(routeContext));
  }, [dispatch, routeContext]);

  useEffect(() => {
    if (routeContext.publicId) {
      APIService.defaults.headers.common['X-Public-Id'] = routeContext.publicId;
    } else {
      delete APIService.defaults.headers.common['X-Public-Id'];
    }
  }, [routeContext.publicId]);
};

export const useSessionBootstrap = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const error = useSelector(sessionErrorSelector);

  useEffect(() => {
    dispatch(fetchProfileThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const errorObj = Utils.getError(error);

    if (errorObj.status === 503) {
      dispatch(setPreviousRoute(location.pathname));
      history.replace('/maintenance/');
    } else {
      Utils.showErrorToast(errorObj.message);
    }

    dispatch(clearSessionError());
  }, [dispatch, error, history, location.pathname]);
};

export const useMaintenancePolling = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const publicId = useSelector(publicIdSelector);
  const previousRoute = useSelector(previousRouteSelector);

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      try {
        await sessionApi.fetchProfile();

        if (cancelled) {
          return;
        }

        dispatch(fetchProfileThunk());
        history.replace(previousRoute || (publicId ? `/${publicId}/listing/new/` : '/'));
      } catch (error) {
        if (Utils.getError(error).status === 503) {
          timeoutId = window.setTimeout(poll, 5000);
          return;
        }

        history.replace(publicId ? `/${publicId}/listing/new/` : '/');
      }
    };

    timeoutId = window.setTimeout(poll, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, history, previousRoute, publicId]);
};

export const useSessionUser = () => useSelector(userSelector);
